import { httpClient } from './httpClient';

export interface BundleProgressEvent {
  message: string;
  stage: string;
  documentName: string | null;
  timestamp: string;
}

export interface BundleProgress {
  stage: string;
  progress: number;          // 0.0–1.0
  complete: boolean;
  failed: boolean;
  cancelled?: boolean;
  error: string | null;
  events: BundleProgressEvent[];
  startedAtMillis: number;
  bundleId?: string;
  totalPages?: number;
  totalDocuments?: number;
  totalSubDocuments?: number;
}

export interface BundleMetadata {
  id: string;
  caseId: string;
  filename: string;
  totalPages?: number;
  totalDocuments?: number;
  totalSubDocuments?: number;
  batesPrefix?: string | null;
  status: 'GENERATING' | 'READY' | 'FAILED';
  generatedAt: string;
  sizeBytes?: number;
}

interface PresignedDownload {
  url: string;
  filename: string;
  expiresInSeconds: number;
}

/**
 * Client for the paginated bundle pipeline (intelligence-service).
 *
 * Mirrors the chronologyApi shape: async generation kicked off via POST,
 * progress polled, finished PDF fetched directly from S3 via presigned URL.
 */
export const bundleApi = {
  /** Kick off async bundle generation. Resolves immediately. */
  generate: async (caseId: string): Promise<{ status: string; caseId: string }> => {
    return httpClient.post(`/intelligence/bundles/generate/${caseId}`, {});
  },

  /**
   * Poll generation progress. Resolves to null when no generation is tracked
   * for the case (HTTP 204).
   */
  getProgress: async (caseId: string): Promise<BundleProgress | null> => {
    try {
      const data = await httpClient.get<BundleProgress>(`/intelligence/bundles/progress/${caseId}`);
      return data ?? null;
    } catch {
      return null;
    }
  },

  /**
   * Whether a paginated bundle can be generated for this case.
   *
   * Returns false for cases uploaded before sub-document detection moved
   * upstream into processing-service (April 2026); the UI hides the bundle
   * action in that case.
   */
  availability: async (caseId: string): Promise<{ ready: boolean; reason: string }> => {
    try {
      const data = await httpClient.get<{ ready: boolean; reason: string }>(
        `/intelligence/bundles/availability/${caseId}`,
      );
      return data ?? { ready: false, reason: 'unknown' };
    } catch {
      return { ready: false, reason: 'fetch-failed' };
    }
  },

  /** Latest generated bundle metadata (or null if none). */
  getLatest: async (caseId: string): Promise<BundleMetadata | null> => {
    try {
      const data = await httpClient.get<BundleMetadata>(`/intelligence/bundles/${caseId}`);
      return data ?? null;
    } catch {
      return null;
    }
  },

  /**
   * Trigger browser download of the generated bundle PDF.
   *
   * Fetches a short-lived presigned S3 URL from the API, then navigates a
   * hidden anchor to it so the browser downloads straight from S3 — no
   * intermediate blob, no service heap buffering. The filename is enforced by
   * the response-content-disposition baked into the presign.
   */
  download: async (caseId: string): Promise<void> => {
    const presigned = await httpClient.get<PresignedDownload>(
      `/intelligence/bundles/${caseId}/download`,
    );
    if (!presigned?.url) throw new Error('Bundle download URL unavailable');

    const link = document.createElement('a');
    link.href = presigned.url;
    // Cross-origin download attribute is advisory; S3's Content-Disposition wins.
    link.download = presigned.filename ?? '';
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  },

  /** Cooperatively cancel an in-flight generation. */
  cancel: async (caseId: string): Promise<void> => {
    await httpClient.post(`/intelligence/bundles/cancel/${caseId}`, {});
  },
};
